import { useTranslation } from "@sagot/i18n";
import {
  AppLink,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Icon,
} from "@sagot/ui";
import type { KeyboardEvent } from "react";
import { useFormatters } from "../shared/format";
import { ChannelBadge, IdleFlag, LeadAvatar } from "./lead-bits";
import { adjacentStage, leadInterest } from "./lead-logic";
import type { Lead, Stage } from "./lead-types";

interface LeadCardProps {
  lead: Lead;
  stageId: string;
  stages: readonly Stage[];
  canEdit: boolean;
  href: string;
  dragType: string;
  onMove: (stage: Stage) => void;
}

/** Board card: draggable; keyboard users press Alt + ← / → or pick a stage from "Move to…". */
export function LeadCard({ lead, stageId, stages, canEdit, href, dragType, onMove }: LeadCardProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!canEdit || !event.altKey || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return;
    event.preventDefault();
    const next = adjacentStage(stages, stageId, event.key === "ArrowLeft" ? -1 : 1);
    if (next) onMove(next);
  };
  return (
    <article
      draggable={canEdit}
      data-testid="lead-card"
      data-lead-id={lead.id}
      aria-label={lead.name}
      className="rounded-md border border-line bg-surface p-3 shadow-xs transition-shadow hover:shadow-pop data-[draggable=true]:cursor-grab"
      data-draggable={canEdit}
      onDragStart={(event) => {
        event.dataTransfer.setData(dragType, lead.id);
        event.dataTransfer.effectAllowed = "move";
      }}
    >
      <div className="flex items-start gap-2.5">
        <LeadAvatar name={lead.name} className="size-8 text-xs" />
        <div className="min-w-0 flex-1">
          <AppLink href={href} onKeyDown={onKeyDown} className="block truncate text-sm font-semibold text-fg hover:underline">
            {lead.name}
          </AppLink>
          <div className="truncate text-xs text-fg-subtle">{leadInterest(lead) || lead.email}</div>
        </div>
        {canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" iconOnly aria-label={t("engage.leads.board.moveMenu", { name: lead.name })} onKeyDown={onKeyDown}>
                <Icon name="more-horizontal" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("engage.leads.board.moveTo")}</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={stageId}
                onValueChange={(value) => {
                  const stage = stages.find((s) => s.id === value);
                  if (stage && stage.id !== stageId) onMove(stage);
                }}
              >
                {stages.map((stage) => (
                  <DropdownMenuRadioItem key={stage.id} value={stage.id}>
                    <span className="flex-1">{stage.name}</span>
                    <DropdownMenuItemIndicator>
                      <Icon name="check" className="size-4 text-primary-ink" />
                    </DropdownMenuItemIndicator>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <ChannelBadge channel={lead.source_channel} />
        {lead.idle ? <IdleFlag /> : null}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-fg-subtle">
        <span className={lead.owner_name ? "truncate" : "truncate italic"}>{lead.owner_name ?? t("engage.leads.unassigned")}</span>
        <span className="shrink-0">{format.ago(lead.created_at)}</span>
      </div>
    </article>
  );
}
